import { useEffect, useState } from 'react';
import { Loader2, Truck, Star, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/services/api';
import { money } from './orderStatus';

export const BookShipmentDialog = ({ order, open, onOpenChange, onBooked }) => {
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [courierId, setCourierId] = useState(null);
  const [opts, setOpts] = useState({ schedule_pickup: true, generate_label: true });

  useEffect(() => {
    if (!open || !order?.id) return;
    setLoading(true); setError(''); setData(null); setCourierId(null);
    api.get(`/ecom-admin/orders/${order.id}/couriers`)
      .then((res) => {
        setData(res.data);
        const rec = (res.data.couriers || []).find((c) => c.recommended) || res.data.couriers?.[0];
        setCourierId(rec?.courier_id ?? null);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Could not load couriers'))
      .finally(() => setLoading(false));
  }, [open, order?.id]);

  const book = async () => {
    setBooking(true);
    try {
      const res = await api.post(`/ecom-admin/orders/${order.id}/book-shipment`, { courier_id: courierId, ...opts });
      toast.success(`Shipment booked · AWB ${res.data.order?.awb_code || ''}`);
      onBooked?.(res.data.order);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="book-shipment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5" /> Book Shipment · {order?.order_number}</DialogTitle>
          <DialogDescription>
            Pick a courier. Cheapest is pre-selected. {data && <>Parcel {data.parcel_weight_kg} kg · pickup {data.pickup_pincode} → {order?.delivery_address?.pincode}</>}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Checking serviceable couriers…</div>
        ) : error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="courier-error">{error}</div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto divide-y rounded-md border" data-testid="courier-list">
            {(data?.couriers || []).map((c) => (
              <button type="button" key={c.courier_id} onClick={() => setCourierId(c.courier_id)}
                className={`w-full text-left px-4 py-3 flex items-center justify-between gap-4 transition-colors ${courierId === c.courier_id ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                data-testid={`courier-${c.courier_id}`}>
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {c.courier_name}
                    {c.recommended && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Cheapest</Badge>}
                    {c.pickup_availability === 'Yes' && <Badge variant="outline" className="text-[10px]">Pickup today</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {c.etd ? `ETA ${c.etd}` : c.estimated_delivery_days ? `${c.estimated_delivery_days} days` : 'ETA n/a'}</span>
                    {c.rating ? <span className="flex items-center gap-1"><Star className="h-3 w-3" /> {Number(c.rating).toFixed(1)}</span> : null}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{money(c.rate)}</div>
                  <div className={`h-4 w-4 rounded-full border ml-auto mt-1 ${courierId === c.courier_id ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`} />
                </div>
              </button>
            ))}
            {data && data.couriers?.length === 0 && <div className="p-4 text-sm text-muted-foreground">No courier serves this pincode.</div>}
          </div>
        )}

        <div className="flex gap-6 text-sm">
          <label className="flex items-center gap-2"><Checkbox checked={opts.schedule_pickup} onCheckedChange={(v) => setOpts({ ...opts, schedule_pickup: !!v })} data-testid="opt-pickup" /> Schedule pickup</label>
          <label className="flex items-center gap-2"><Checkbox checked={opts.generate_label} onCheckedChange={(v) => setOpts({ ...opts, generate_label: !!v })} data-testid="opt-label" /> Generate label</label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={book} disabled={booking || loading || !!error} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-book-btn">
            {booking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
            {courierId ? 'Assign AWB & Book' : 'Let Shiprocket pick & Book'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
